import { useState } from 'react'
import { ChevronDown, ChevronUp, Clock, User, PenLine, Plus } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui'
import { usePrivacyAudit } from '@/hooks/use-privacy'
import { formatDateTime } from '@/lib/utils/date'
import {
  PrivacyAuditEntry,
  FieldChange,
  FIELD_LABELS,
  ACTION_LABELS,
} from '@/models/privacy'

interface AuditLogSectionProps {
  subjectId: string
}

const INITIAL_DISPLAY_COUNT = 5

export function AuditLogSection({ subjectId }: AuditLogSectionProps) {
  const [showAll, setShowAll] = useState(false)
  const { data, isLoading, error } = usePrivacyAudit(subjectId)

  if (isLoading) {
    return (
      <div className="py-4">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
          Audit Log
        </h4>
        <div className="flex items-center justify-center py-4">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
            Loading audit history...
          </span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-4">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
          Audit Log
        </h4>
        <p className="text-sm text-red-500 dark:text-red-400">
          Failed to load audit history
        </p>
      </div>
    )
  }

  const entries = data?.entries ?? []
  const displayedEntries = showAll ? entries : entries.slice(0, INITIAL_DISPLAY_COUNT)
  const hasMore = entries.length > INITIAL_DISPLAY_COUNT

  if (entries.length === 0) {
    return (
      <div className="py-4">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
          Audit Log
        </h4>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No audit history available
        </p>
      </div>
    )
  }

  return (
    <div className="py-4">
      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
        Audit Log
      </h4>
      <div className="space-y-3">
        {displayedEntries.map((entry) => (
          <AuditEntryRow key={entry.id} entry={entry} />
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="mt-3 flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          {showAll ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show {entries.length - INITIAL_DISPLAY_COUNT} more
            </>
          )}
        </button>
      )}
    </div>
  )
}

function AuditEntryRow({ entry }: { entry: PrivacyAuditEntry }) {
  const actionLabel = ACTION_LABELS[entry.action] || entry.action
  const isCreated = entry.action === 'created'
  const ActionIcon = isCreated ? Plus : PenLine

  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center justify-center h-6 w-6 rounded-full ${
              isCreated
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
            }`}
          >
            <ActionIcon className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm font-medium text-slate-900 dark:text-white">
            {actionLabel}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <Clock className="h-3 w-3" />
          {formatDateTime(entry.created_at)}
        </div>
      </div>

      {entry.user_email && (
        <div className="mt-2 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <User className="h-3 w-3" />
          {entry.user_email}
        </div>
      )}

      {entry.changes && Object.keys(entry.changes).length > 0 && (
        <div className="mt-2 space-y-1">
          {Object.entries(entry.changes).map(([field, change]) => (
            <FieldChangeRow key={field} field={field} change={change} />
          ))}
        </div>
      )}
    </div>
  )
}

function FieldChangeRow({ field, change }: { field: string; change: FieldChange }) {
  const fieldLabel = FIELD_LABELS[field] || field
  const oldValue = formatValue(change.old)
  const newValue = formatValue(change.new)

  return (
    <div className="text-xs text-slate-600 dark:text-slate-300 pl-8">
      <span className="font-medium">{fieldLabel}:</span>{' '}
      {oldValue !== null && (
        <>
          <span className="text-red-600 dark:text-red-400 line-through">{oldValue}</span>
          <span className="mx-1 text-slate-400">→</span>
        </>
      )}
      <span className="text-green-600 dark:text-green-400">{newValue}</span>
    </div>
  )
}

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') {
    // Check if it's a date string
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return formatDateTime(value)
    }
    return value || '(empty)'
  }
  return String(value)
}
